// @flow

import React, { PureComponent } from 'react';
import { QueryRenderer, graphql } from 'react-relay';
import styled from 'styled-components';
import {
  Route,
  Switch,
  Redirect,
  Link,
  type Match,
  type RouterHistory,
} from 'react-router-dom';
import { ContextMenuTrigger, ContextMenu, MenuItem } from 'react-contextmenu';
import relayEnvironment from 'shared/gql/relayEnvironment';
import ReactTooltip from 'react-tooltip';
import { i18n } from 'shared/utils';
import config from 'main-app/config';
import JobUpdatedSubscription from 'main-app/subscriptions/JobUpdated';
import Page from 'main-app/components/Page';
import PageHeader from 'main-app/components/PageHeader';
import PageDetailsSummary from 'main-app/components/PageDetailsSummary';
import Loader from 'shared/components/common/Loader';
import Button from 'shared/components/common/Button';
import TabNav from 'shared/components/common/TabNav';
import JobStatusPill from 'shared/components/common/Pill/JobStatusPill';
import Icon from 'shared/components/common/Icon';
import UserWidget from 'shared/components/common/QueryWidget/UserWidget';
import InfoWidget from 'shared/components/common/InfoWidget';
import AddUpdateJobModal from 'main-app/components/AddUpdateJobModal';
import CloseJobModal from 'main-app/components/CloseJobModal';
import ReopenJobModal from 'main-app/components/ReopenJobModal';
import DeleteJobModal from 'main-app/components/DeleteJobModal';
import moment from 'moment';
import JobLinkedItems from './JobLinkedItems';
import JobDetails from './JobDetails';
import JobAttachments from './JobAttachments';
import ImportedJobDisclaimer from './ImportedJobDisclaimer';
import Runs from './Runs';
// import JobProgressPanel from './JobProgressPanel';
// import JobTasks from './JobTasks';
// import Tags from './Tags';
import JobSubComponents from './JobSubComponents';
import Logo from 'main-app/components/PageHeaderLogo/img/logo.svg';
import {
  PrintWrapper,
  PrintOnly,
  NoPrint,
  PrintPage,
  PrintDocHeader,
  PrintDocHeaderTitle,
  PrintDocHeaderCompany,
  PrintInfoGrid,
  PrintInfoColumn,
  PrintInfoRow,
  PrintInfoLabel,
  PrintNotes,
  PrintItemTable,
  PrintDocFooter,
  PrintSignatureGrid,
  PrintSignatureColumn,
  PrintSignatureLine,
  PrintSignatureUnderline,
} from 'shared/components/common/PrintLayout';

const NotesWrapper = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 90vw;

  @media (min-width: 1000px) {
    max-width: 200px;
  }
`;

type Props = {
  history: RouterHistory,
  match: {
    ...Match,
    params: {
      id: string,
    },
  },
};

type State = {
  updateJobModalOpen: boolean,
  duplicateJobModalOpen: boolean,
  closeJobModalOpen: boolean,
  reopenJobModalOpen: boolean,
  deleteJobModalOpen: boolean,
};

const ContentWrapper = styled.div``;

const ExportButtonWrapper = styled.div`
  display: none;

  @media (min-width: 1000px) {
    display: block;
  }
`;

class Job extends PureComponent<Props, State> {
  menuTriggerRef = null;
  jobUpdatedSubscription = null;

  state = {
    updateJobModalOpen: false,
    closeJobModalOpen: false,
    duplicateJobModalOpen: false,
    reopenJobModalOpen: false,
    deleteJobModalOpen: false,
  };

  componentDidMount() {
    const { match } = this.props;

    this.jobUpdatedSubscription = JobUpdatedSubscription.commit({
      variables: {
        id: match.params.id,
      },
    });
  }

  componentWillUnmount() {
    if (this.jobUpdatedSubscription) {
      this.jobUpdatedSubscription.dispose();
    }
  }

  handleViewImportAttachment = (urlPath: string) => {
    window.open(config.cdnUrl + '/' + urlPath);
  };

  handleOpenDuplicateJobModal = () => {
    this.setState({
      duplicateJobModalOpen: true,
    });
  };

  handleOpenUpdateJobModal = () => {
    this.setState({
      updateJobModalOpen: true,
    });
  };

  handleOpenCloseJobModal = () => {
    this.setState({
      closeJobModalOpen: true,
    });
  };

  handleOpenReopenJobModal = () => {
    this.setState({
      reopenJobModalOpen: true,
    });
  };

  handleOpenDeleteJobModal = () => {
    this.setState({
      deleteJobModalOpen: true,
    });
  };

  handleDeleteSuccess = () => {
    const { history } = this.props;

    history.replace('/jobs');
  };

  handleCloseAllModals = () => {
    this.setState({
      updateJobModalOpen: false,
      closeJobModalOpen: false,
      duplicateJobModalOpen: false,
      reopenJobModalOpen: false,
      deleteJobModalOpen: false,
    });
  };

  handleExportJobTicket = () => {
    const { match } = this.props;

    window.open(`/api/v1/print/job-ticket/${match.params.id}`);
  };

  handlePrintJobTicket = () => {
    window.print();
  };

  render() {
    const { match, history } = this.props;
    const {
      updateJobModalOpen,
      closeJobModalOpen,
      duplicateJobModalOpen,
      reopenJobModalOpen,
      deleteJobModalOpen,
    } = this.state;

    return (
      <QueryRenderer
        environment={relayEnvironment}
        dataFrom="STORE_THEN_NETWORK"
        query={graphql`
          query JobQuery($id: ID!) {
            job(id: $id) {
              id
              jobNumber
              importJobNumber
              importPreviousJobNumber
              status
              name
              notes
              forceClosedAt
              createdAt
              importAttachment {
                id
                urlPath
              }
              user {
                ...UserWidget_user
              }
              order {
                id
                orderNumber
                customerOrderNumber
                requiredAt
                promisedAt
                shippingAddressLine1
                shippingAddressLine2
                shippingAddressLine3
                shippingAddressCity
                shippingAddressState
                shippingAddressZip
                customer {
                  id
                  name
                }
              }
              workflow {
                id
                name
              }
              yields(first: null) {
                edges {
                  node {
                    id
                    quantity
                    finalQuantity
                    item {
                      id
                      name
                      itemNumber
                      description
                    }
                  }
                }
              }
              ...JobDetails_job
              ...Runs_job
              ...AddUpdateJobModal_job
              ...CloseJobModal_job
              ...ReopenJobModal_job
              ...DeleteJobModal_job
              ...JobLinkedItems_job
              ...JobSubComponents_job
            }

            forms(types: [ITEM]) {
              edges {
                node {
                  id
                  ...JobDetails_defaultForm
                }
              }
            }

            viewer {
              id
              permissions
              company {
                id
                name
                addressLine1
                addressLine2
                addressCity
                addressState
                addressZip
                addressCountry
                phone
              }
            }
          }
        `}
        variables={{
          id: match.params.id,
        }}
        render={query => (
          <Page>
            <PageHeader
              intro={i18n.t('JOB TICKET')}
              title={
                query.props
                  ? query.props.job.jobNumber
                  : i18n.t('Job Details Loading...')
              }
              right={
                query.props && [
                  <JobStatusPill status={query.props.job.status} />,
                  <ExportButtonWrapper>
                    <Button
                      theme="border-white"
                      onClick={this.handlePrintJobTicket}
                    >
                      {i18n.t('Print Job Ticket')}
                    </Button>
                  </ExportButtonWrapper>,
                  <ContextMenuTrigger
                    id="job-details-menu"
                    ref={r => (this.menuTriggerRef = r)}
                  >
                    <Icon
                      type="circle-context-menu"
                      onClick={e => {
                        if (this.menuTriggerRef) {
                          this.menuTriggerRef.handleContextClick(e);
                        }
                      }}
                    />
                  </ContextMenuTrigger>,
                ]
              }
            />
            {!query.props ? (
              <Loader />
            ) : (
              <PrintWrapper>
              <PrintOnly>
                <PrintPage>
                  <PrintDocHeader>
                    <PrintDocHeaderTitle>
                      <Logo style={{ width: 100, height: 'auto', display: 'block', marginBottom: 4 }} />
                      {i18n.t('Job Ticket')}
                    </PrintDocHeaderTitle>
                    <PrintDocHeaderCompany>{query.props.viewer && query.props.viewer.company && query.props.viewer.company.name}</PrintDocHeaderCompany>
                  </PrintDocHeader>

                  <PrintInfoGrid>
                    <PrintInfoColumn>
                      <PrintInfoRow>
                        <PrintInfoLabel>{i18n.t('Job #')}</PrintInfoLabel>
                        <div>{query.props.job.jobNumber}</div>
                      </PrintInfoRow>
                      <PrintInfoRow>
                        <PrintInfoLabel>{i18n.t('Date')}</PrintInfoLabel>
                        <div>{moment(query.props.job.createdAt).format('MM/DD/YYYY')}</div>
                      </PrintInfoRow>
                      <PrintInfoRow>
                        <PrintInfoLabel>{i18n.t('Project')}</PrintInfoLabel>
                        <div>{query.props.job.name}</div>
                      </PrintInfoRow>
                      {query.props.job.workflow && (
                        <PrintInfoRow>
                          <PrintInfoLabel>{i18n.t('Workflow')}</PrintInfoLabel>
                          <div>{query.props.job.workflow.name}</div>
                        </PrintInfoRow>
                      )}
                      <PrintInfoRow>
                        <PrintInfoLabel>{i18n.t('Status')}</PrintInfoLabel>
                        <div>{query.props.job.status}</div>
                      </PrintInfoRow>
                    </PrintInfoColumn>
                    <PrintInfoColumn>
                      {query.props.job.order && (
                        <>
                          <PrintInfoRow>
                            <PrintInfoLabel>{i18n.t('Customer')}</PrintInfoLabel>
                            <div>{query.props.job.order.customer.name}</div>
                          </PrintInfoRow>
                          <PrintInfoRow>
                            <PrintInfoLabel>{i18n.t('Order #')}</PrintInfoLabel>
                            <div>{query.props.job.order.orderNumber}</div>
                          </PrintInfoRow>
                          {query.props.job.order.customerOrderNumber && (
                            <PrintInfoRow>
                              <PrintInfoLabel>{i18n.t('Customer PO')}</PrintInfoLabel>
                              <div>{query.props.job.order.customerOrderNumber}</div>
                            </PrintInfoRow>
                          )}
                          {query.props.job.order.requiredAt && (
                            <PrintInfoRow>
                              <PrintInfoLabel>{i18n.t('Required Date')}</PrintInfoLabel>
                              <div>{moment(query.props.job.order.requiredAt).format('MM/DD/YYYY')}</div>
                            </PrintInfoRow>
                          )}
                        </>
                      )}
                      {query.props.viewer && query.props.viewer.company && (
                        <>
                          <PrintInfoRow>
                            <PrintInfoLabel>{i18n.t('Manufacturer')}</PrintInfoLabel>
                            <div>{query.props.viewer.company.name}</div>
                          </PrintInfoRow>
                          {(query.props.viewer.company.addressLine1 || query.props.viewer.company.addressCity) && (
                            <PrintInfoRow>
                              <PrintInfoLabel>{i18n.t('Address')}</PrintInfoLabel>
                              <div>
                                {query.props.viewer.company.addressLine1 && <div>{query.props.viewer.company.addressLine1}</div>}
                                {query.props.viewer.company.addressLine2 && <div>{query.props.viewer.company.addressLine2}</div>}
                                {(query.props.viewer.company.addressCity || query.props.viewer.company.addressState || query.props.viewer.company.addressZip) && (
                                  <div>{[query.props.viewer.company.addressCity, query.props.viewer.company.addressState, query.props.viewer.company.addressZip].filter(Boolean).join(', ')}</div>
                                )}
                              </div>
                            </PrintInfoRow>
                          )}
                          {query.props.viewer.company.phone && (
                            <PrintInfoRow>
                              <PrintInfoLabel>{i18n.t('Phone')}</PrintInfoLabel>
                              <div>{query.props.viewer.company.phone}</div>
                            </PrintInfoRow>
                          )}
                        </>
                      )}
                    </PrintInfoColumn>
                  </PrintInfoGrid>

                  <PrintItemTable>
                    <thead>
                      <tr>
                        <th style={{ width: '5%' }}>#</th>
                        <th style={{ textAlign: 'left' }}>{i18n.t('Part #')}</th>
                        <th style={{ textAlign: 'left' }}>{i18n.t('Part Description')}</th>
                        <th>{i18n.t('Quantity')}</th>
                        <th>{i18n.t('Final Qty')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {((query.props.job.yields && query.props.job.yields.edges) || []).map((edge, idx) => (
                        <tr key={edge.node.id}>
                          <td>{idx + 1}</td>
                          <td>{edge.node.item.itemNumber || edge.node.item.name}</td>
                          <td>{edge.node.item.description || edge.node.item.name}</td>
                          <td style={{ textAlign: 'center' }}>{parseFloat(edge.node.quantity || 0).toFixed(2)}</td>
                          <td style={{ textAlign: 'center' }}>{parseFloat(edge.node.finalQuantity || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                      {(!query.props.job.yields || !query.props.job.yields.edges || query.props.job.yields.edges.length === 0) && (
                        <tr>
                          <td colSpan={5} style={{ textAlign: 'center', color: '#999' }}>{i18n.t('No items')}</td>
                        </tr>
                      )}
                    </tbody>
                  </PrintItemTable>

                  {query.props.job.notes && (
                    <PrintNotes>
                      <PrintInfoLabel>{i18n.t('Notes')}</PrintInfoLabel>
                      <div>{query.props.job.notes}</div>
                    </PrintNotes>
                  )}

                  <PrintSignatureGrid>
                    <PrintSignatureColumn>
                      <PrintSignatureLine>
                        <label>{i18n.t('Started by:')}</label>
                        <PrintSignatureUnderline />
                      </PrintSignatureLine>
                      <PrintSignatureLine>
                        <label>{i18n.t('Completed by:')}</label>
                        <PrintSignatureUnderline />
                      </PrintSignatureLine>
                      <PrintSignatureLine>
                        <label>{i18n.t('Date:')}</label>
                        <PrintSignatureUnderline />
                      </PrintSignatureLine>
                    </PrintSignatureColumn>
                    <PrintSignatureColumn>
                      <PrintSignatureLine>
                        <label>{i18n.t('QC by:')}</label>
                        <PrintSignatureUnderline />
                      </PrintSignatureLine>
                      <PrintSignatureLine>
                        <label>{i18n.t('Date:')}</label>
                        <PrintSignatureUnderline />
                      </PrintSignatureLine>
                    </PrintSignatureColumn>
                  </PrintSignatureGrid>

                  <PrintDocFooter>
                    <span>{i18n.t('Created')} {moment(query.props.job.createdAt).format('MM/DD/YYYY')}</span>
                  </PrintDocFooter>
                </PrintPage>
              </PrintOnly>

              <NoPrint>
              <>
                <PageDetailsSummary
                  items={[
                    query.props.job.importJobNumber && (
                      <InfoWidget
                        title={i18n.t('Original Job #')}
                        content={query.props.job.importJobNumber}
                      />
                    ),
                    query.props.job.importPreviousJobNumber && (
                      <InfoWidget
                        title={i18n.t('Prev. Original Job #')}
                        content={query.props.job.importPreviousJobNumber}
                      />
                    ),
                    query.props.job.user && (
                      <UserWidget
                        user={query.props.job.user}
                        title={i18n.t('Job Owner')}
                      />
                    ),
                    <InfoWidget
                      title={i18n.t('Project')}
                      content={query.props.job.name}
                    />,
                    query.props.job.order && (
                      <InfoWidget
                        title={i18n.t('Customer')}
                        content={
                          <Link
                            to={`/customer/${query.props.job.order.customer.id}`}
                          >
                            {query.props.job.order.customer.name}
                          </Link>
                        }
                      />
                    ),
                    query.props.job.order && (
                      <InfoWidget
                        title={i18n.t('Order #')}
                        content={
                          <Link to={`/order/${query.props.job.order.id}`}>
                            {query.props.job.order.orderNumber}
                          </Link>
                        }
                      />
                    ),
                    query.props.job.notes && (
                      <div data-for={query.props.job.id + '-notes'} data-tip>
                        <InfoWidget
                          title={i18n.t('Notes')}
                          content={
                            <>
                              <NotesWrapper>
                                {query.props.job.notes}
                              </NotesWrapper>
                              <ReactTooltip
                                place="bottom"
                                effect="solid"
                                type="info"
                                id={query.props.job.id + '-notes'}
                              >
                                {query.props.job.notes}
                              </ReactTooltip>
                            </>
                          }
                        />
                      </div>
                    ),
                    <InfoWidget
                      title={i18n.t('Created')}
                      content={moment(query.props.job.createdAt).format(
                        'MM/DD/YYYY',
                      )}
                    />,
                    query.props.job.workflow.name && (
                      <InfoWidget
                        title={i18n.t('Workflow')}
                        content={query.props.job.workflow.name}
                      />
                    ),
                    // // TODO: replace with query.props.job.workflow
                    // true && (
                    //   <InfoWidget title={i18n.t('Tags')} content={<Tags />} />
                    // ),
                  ]}
                />
                {query.props.job.importJobNumber ? (
                  <ImportedJobDisclaimer
                    onViewAttachment={
                      query.props.job.importAttachment &&
                      (() =>
                        this.handleViewImportAttachment(
                          query.props.job.importAttachment.urlPath,
                        ))
                    }
                  />
                ) : null}
                <TabNav
                  history={history}
                  activePath={match.url}
                  tabs={[
                    {
                      label: i18n.t('Overview'),
                      path: `/job/${match.params.id}`,
                    },
                    {
                      label: i18n.t('Runs'),
                      path: `/job/${match.params.id}/runs`,
                    },
                    {
                      label: i18n.t('Components'),
                      path: `/job/${match.params.id}/components`,
                    },
                    {
                      label: i18n.t('Linked Tooling'),
                      path: `/job/${match.params.id}/linked-items`,
                    },
                    {
                      label: i18n.t('Attachments'),
                      path: `/job/${match.params.id}/attachments`,
                    },
                  ]}
                />
                <ContentWrapper>
                  <Switch>
                    <Route
                      exact
                      path="/job/:id"
                      render={props => (
                        <JobDetails
                          {...props}
                          job={query.props.job}
                          defaultForm={
                            query.props?.forms.edges[0]?.node || null
                          }
                          // defaultForm={query.props.forms.edges.map(
                          //   ({ node }) => node,
                          // )}
                        />
                      )}
                    />
                    <Route
                      exact
                      path="/job/:id/runs"
                      render={props => (
                        <Runs {...props} job={query.props.job} />
                      )}
                    />
                    <Route
                      exact
                      path="/job/:id/components"
                      render={props => (
                        <JobSubComponents {...props} job={query.props.job} />
                      )}
                    />
                    <Route
                      exact
                      path="/job/:id/linked-items"
                      render={props => (
                        <JobLinkedItems {...props} job={query.props.job} />
                      )}
                    />
                    <Route
                      exact
                      path="/job/:id/attachments"
                      component={JobAttachments}
                    />
                    <Route
                      exact
                      render={() => <Redirect to={`/job/${match.params.id}`} />}
                    />
                  </Switch>
                </ContentWrapper>
                <ContextMenu id="job-details-menu">
                  {query.props.viewer.permissions.includes('UPDATE_JOB') && (
                    <MenuItem onClick={this.handleOpenUpdateJobModal}>
                      {i18n.t('Edit Job Ticket')}
                    </MenuItem>
                  )}
                  <MenuItem onClick={this.handleOpenDuplicateJobModal}>
                    {i18n.t('Duplicate Job Ticket')}
                  </MenuItem>
                  {query.props.viewer.permissions.includes('UPDATE_JOB') &&
                    query.props.job.status === 'OPEN' && (
                      <MenuItem onClick={this.handleOpenCloseJobModal}>
                        {i18n.t('Close Job Ticket')}
                      </MenuItem>
                    )}
                  {query.props.viewer.permissions.includes('UPDATE_JOB') &&
                    query.props.job.status === 'CLOSED' && (
                      <MenuItem onClick={this.handleOpenReopenJobModal}>
                        {i18n.t('Re-Open Job Ticket')}
                      </MenuItem>
                    )}
                  {/* duplicate job ticket here */}
                  {query.props.job.order ? (
                    <MenuItem
                      onClick={() =>
                        history.push(`/order/${query.props.job.order.id}`)
                      }
                    >
                      {i18n.t('View Order')}
                    </MenuItem>
                  ) : null}
                  <MenuItem onClick={this.handleOpenDeleteJobModal}>
                    {i18n.t('Delete Job')}
                  </MenuItem>
                </ContextMenu>
                {updateJobModalOpen && (
                  <AddUpdateJobModal
                    onClose={this.handleCloseAllModals}
                    job={query.props.job}
                    workflow={null}
                  />
                )}
                {duplicateJobModalOpen && (
                  <AddUpdateJobModal
                    onClose={this.handleCloseAllModals}
                    onSuccess={job => history.push(`/job/${job.id}`)}
                    job={query.props.job}
                    workflow={null}
                    duplicateJob
                  />
                )}
                {closeJobModalOpen && (
                  <CloseJobModal
                    onClose={this.handleCloseAllModals}
                    job={query.props.job}
                  />
                )}
                {reopenJobModalOpen && (
                  <ReopenJobModal
                    onClose={this.handleCloseAllModals}
                    job={query.props.job}
                  />
                )}
                {deleteJobModalOpen && (
                  <DeleteJobModal
                    history={history}
                    job={query.props.job}
                    onClose={this.handleCloseAllModals}
                    onSuccess={this.handleDeleteSuccess}
                  />
                )}
              </>
              </NoPrint>
              </PrintWrapper>
            )}
          </Page>
        )}
      />
    );
  }
}

export default Job;
